import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import AdminLayout from '../components/AdminLayout';
import ExportToolbar from '../components/ExportToolbar';
import { exportCSV, exportJSON, todayStr } from '../utils/exportUtils';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function fmt$(v) {
  if (!v || isNaN(v) || v === 0) return '$0';
  if (v >= 1e6) return '$' + (v / 1e6).toFixed(2) + 'M';
  if (v >= 1e3) return '$' + Math.round(v / 1e3) + 'K';
  return '$' + Math.round(v).toLocaleString();
}
function fmtFull$(v) {
  if (!v || isNaN(v)) return '—';
  return '$' + Math.round(parseFloat(v)).toLocaleString();
}

const card = { backgroundColor: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const th = { padding: '10px 12px', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '1px solid #f3f4f6', backgroundColor: '#f9fafb' };
const td = { padding: '11px 12px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f9fafb', whiteSpace: 'nowrap' };

const QUICK_RANGES = [
  { label: 'All Time', days: null },
  { label: 'Last 30d', days: 30 },
  { label: 'Last 90d', days: 90 },
  { label: 'Last 6mo', days: 180 },
  { label: 'Last 12mo', days: 365 },
];

const DONUT_COLORS = ['#3a6ad9', '#3a6ad9', '#acf44e'];

export default function AdminReportsRevenue() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeQuick, setActiveQuick] = useState('All Time');

  const load = useCallback(async (sd, ed) => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (sd && ed) { params.start_date = sd; params.end_date = ed; }
      const res = await axios.get(`${API}/api/admin/reports/revenue`, { headers, params });
      setLoans(res.data.loans || []);
      setSummary(res.data.summary || null);
      setMonthly(res.data.monthly_breakdown || []);
    } catch (err) {
      if (err?.response?.status === 401) { navigate('/admin/login'); return; }
      setError(err?.response?.data?.error || err.message);
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load('', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyQuick = (q) => {
    setActiveQuick(q.label);
    if (!q.days) { setStartDate(''); setEndDate(''); load('', ''); return; }
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - q.days * 86400000).toISOString().slice(0, 10);
    setStartDate(start); setEndDate(end); load(start, end);
  };

  const donutData = summary ? [
    { name: 'Program Fees', value: summary.total_program_fees || 0 },
    { name: 'RE Commission', value: summary.total_re_commission || 0 },
  ] : [];

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Revenue Reports</h1>
            <p style={{ fontSize: '13px', color: '#6b7280' }}>Closed loan revenue by stream</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <ExportToolbar
              disabled={loans.length === 0}
              onExportCSV={() => {
                const revenueCSVColumns = [
                  { label: 'Loan #', key: 'loan_number' },
                  { label: 'Borrower', key: 'borrower_name' },
                  { label: 'Close Date', accessor: (r) => r.closed_date ? new Date(r.closed_date).toLocaleDateString() : '' },
                  { label: 'Purchase Price', accessor: (r) => String(Math.round(parseFloat(r.purchase_price) || 0)) },
                  { label: 'Program Fee', accessor: (r) => String(Math.round(parseFloat(r.program_fee) || 0)) },
                  { label: 'RE Commission', accessor: (r) => String(Math.round(parseFloat(r.re_commission) || 0)) },
                  { label: 'Total Revenue', accessor: (r) => String(Math.round(parseFloat(r.total_revenue) || 0)) },
                ];
                const dataWithTotals = summary ? [
                  ...loans,
                  { loan_number: 'TOTALS', borrower_name: '', closed_date: '', purchase_price: '', program_fee: summary.total_program_fees, re_commission: summary.total_re_commission, total_revenue: summary.total_revenue },
                ] : loans;
                exportCSV(dataWithTotals, revenueCSVColumns, `doorly-revenue-report-${todayStr()}`);
              }}
              onExportJSON={() => exportJSON(
                { report: 'Revenue', generated: new Date().toISOString(), summary, loans },
                `doorly-revenue-report-${todayStr()}`
              )}
            />
            {QUICK_RANGES.map((q) => (
              <button key={q.label} onClick={() => applyQuick(q)}
                style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid',
                  backgroundColor: activeQuick === q.label ? '#111827' : '#fff',
                  color: activeQuick === q.label ? '#fff' : '#374151',
                  borderColor: activeQuick === q.label ? '#111827' : '#e5e7eb' }}>
                {q.label}
              </button>
            ))}
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setActiveQuick(''); }}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
            <span style={{ color: '#9ca3af', fontSize: '12px' }}>to</span>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setActiveQuick(''); }}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px' }} />
            <button onClick={() => { setActiveQuick(''); load(startDate, endDate); }}
              style={{ padding: '6px 14px', borderRadius: '8px', backgroundColor: '#3a6ad9', color: '#fff', border: 'none', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
              Apply
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '80px', textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#ff082f' }}>{error}</div>
        ) : (
          <>
            {/* KPI cards */}
            {summary && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: 'Loans Closed', value: summary.total_loans },
                  { label: 'Total Revenue', value: fmt$(summary.total_revenue) },
                  { label: 'Revenue MTD', value: fmt$(summary.revenue_mtd) },
                  { label: 'Avg Rev / Loan', value: fmt$(summary.avg_revenue_per_loan) },
                  { label: 'Program Fees', value: fmt$(summary.total_program_fees) },
                  { label: 'RE Commission', value: fmt$(summary.total_re_commission) },
                ].map((k) => (
                  <div key={k.label} style={{ ...card, padding: '14px 16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{k.label}</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#111827' }}>{k.value}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Charts row */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '24px' }}>
              {/* Monthly bar chart */}
              <div style={card}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Revenue by Month</h3>
                {monthly.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px', fontSize: '13px' }}>No closed loans yet</div>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={monthly} margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => fmt$(v)} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [fmt$(v)]} />
                      <Legend />
                      <Bar dataKey="program_fees" name="Program Fees" fill="#3a6ad9" stackId="a" />
                      <Bar dataKey="re_commission" name="RE Commission" fill="#acf44e" stackId="a" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Donut breakdown */}
              <div style={card}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '16px' }}>Revenue Breakdown</h3>
                {donutData.every((d) => d.value === 0) ? (
                  <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px', fontSize: '13px' }}>No revenue data</div>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={donutData} dataKey="value" innerRadius={50} outerRadius={80} paddingAngle={3}>
                          {donutData.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => [fmt$(v)]} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {donutData.map((d, i) => (
                        <div key={d.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: DONUT_COLORS[i] }} />
                            <span style={{ color: '#374151' }}>{d.name}</span>
                          </div>
                          <span style={{ fontWeight: '600', color: '#111827' }}>{fmt$(d.value)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Loan table */}
            <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Closed Loans — Revenue Detail ({loans.length})</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Loan #', 'Borrower', 'Closed', 'Purchase Price', 'Program Fee', 'RE Commission', 'Total Revenue'].map((h) => (
                        <th key={h} style={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loans.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#9ca3af', padding: '48px' }}>No closed loans</td></tr>
                    ) : loans.map((l) => (
                      <tr key={l.id}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <td style={td}><span style={{ color: '#3a6ad9', fontWeight: '600', fontSize: '13px' }}>{l.loan_number || '—'}</span></td>
                        <td style={td}>{l.borrower_name || '—'}</td>
                        <td style={td}>{l.closed_date ? new Date(l.closed_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</td>
                        <td style={td}>{fmtFull$(l.purchase_price)}</td>
                        <td style={td}>{fmtFull$(l.program_fee)}</td>
                        <td style={td}>{fmtFull$(l.re_commission)}</td>
                        <td style={{ ...td, fontWeight: '700', color: '#acf44e' }}>{fmtFull$(l.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
